import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useGlobal from "src/hooks/api/useGlobal";
import useLanguage from "src/hooks/api/useLanguage";
import useStudentPool from "src/hooks/api/useStudentpool";
import useIssuer from "src/hooks/selectors/useIssuer";
import useModules from "src/hooks/selectors/useModules";
import useSignature from "src/hooks/selectors/useSignature";
import { getCookie } from "src/lib/api";
import getTokenName from "src/lib/getTokenName";
import i18n from "src/lib/i18n";
import { updateLanguage } from "src/redux/language";
import { updateModules } from "src/redux/modules";
import { updateSupport } from "src/redux/support";
import { updateProfile } from "src/redux/auth";
import useTaskStatuses from "src/hooks/api/tasks/useTaskStatuses";
import useUsers from "src/hooks/api/services/users/useUsers";
import useCategories from "src/hooks/api/services/categories/useCategories";
import useConnections from "src/hooks/api/services/users/useConnections";
import useLimits from "src/hooks/api/services/auth/useLimits";
import useTeams from "src/hooks/api/services/users/useTeams";

interface Options {
	disableMount?: boolean;
}

function usePrefetch(options?: Options) {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState({
		profile: true,
		member: true,
	});
	const { actions } = useLanguage();
	const { fetchModules } = useModules();
	const { fetchSelectedSignature } = useSignature();
	const { fetchIssuers } = useIssuer();
	const { fetchGlobal } = useGlobal();
	const { fetchStudentPool } = useStudentPool();
	const { actions: taskStatusesActions } = useTaskStatuses();
	const { actions: usersActions } = useUsers();
	const { actions: cateogoriesActions } = useCategories();
	const { actions: connectionsActions } = useConnections();
	const { actions: limitsActions } = useLimits();
	const { actions: teamsActions } = useTeams();

	const fetchProfileWithDefaults = async () => {
		axios
			.get("/auth/profile")
			.then(({ data }) => {
				const user = data?.data;
				dispatch(updateProfile(user));

				if (user?.language) {
					actions.changeLanguage(user?.language?.code);
				}

				if (user.type === "company") {
					taskStatusesActions.get();
					axios
						.get("/company/support/member")
						.then(({ data }) => {
							const member = data?.data;
							dispatch(updateSupport({ member, loading: false }));
						})
						.catch(() => {
							dispatch(updateSupport({ loading: false }));
						});
				}
				if (user.type === "company") {
					fetchModules();
					fetchIssuers();
					// fetchGlobal();
					fetchStudentPool();
					usersActions.list();
					cateogoriesActions.list();
					connectionsActions.list();
					limitsActions.get("");
					teamsActions.list();
				}

				if (user.type === "student") {
					dispatch(
						updateModules({
							loading: false,
						})
					);
					dispatch(updateSupport({ loading: false }));
				}

				fetchSelectedSignature();
			})
			.finally(() =>
				setLoading((l) => ({
					...l,
					profile: false,
				}))
			);
	};

	useEffect(() => {
		if (!options?.disableMount) {
			actions.get();
			fetchGlobal();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!options?.disableMount) {
			const token = getCookie(getTokenName());

			//Set the default i18n language
			dispatch(updateLanguage({ current: i18n.language }));

			if (token) {
				fetchProfileWithDefaults();
			} else {
				setLoading((l) => ({
					...l,
					profile: false,
				}));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		loading: loading.profile,
		fetchProfileWithDefaults,
	};
}

export default usePrefetch;
